import { Alert, Button, Chip, Group, Loader, Select } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useNavigate } from 'react-router-dom';

import { MemoryCard } from 'src/Components/Memories/MemoryCard';
import { MEMORY_KINDS, memoryKindLabel } from 'src/Components/Memories/memoryKinds';
import { PageHeader } from 'src/Components/Navigation/PageHeader';
import useFetchMemories from 'src/Hooks/Queries/useFetchMemories';
import { MemoryKind, MemoryTimelineItemDto } from 'src/Services/API/Memories.api';
import { trackMemoryTimelineViewed } from 'src/Services/Mixpanel/events';
import { decadeLabel, decadeOf } from 'src/Utils/memoryDate';

import styles from './MemoriesTimelinePage.module.scss';

const CURRENT_DECADE = Math.floor(new Date().getFullYear() / 10) * 10;
const UNDATED = 'undated';

const sectionId = (decade: number | typeof UNDATED) => `memories-${decade}`;

const MemoriesTimelinePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [kind, setKind] = useState<MemoryKind | null>(null);
  const [withVoice, setWithVoice] = useState(false);
  const { data, isLoading, isError } = useFetchMemories({
    kind: kind ?? undefined,
    withVoice,
  });
  const isFiltered = kind !== null || withVoice;

  useEffect(() => {
    if (data) trackMemoryTimelineViewed({ memory_count: data.stats.memoryCount });
    // Once per visit, not on every filter change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!data]);

  // Decades from the oldest dated memory to today; empty ones invite someone to tell them.
  const { decades, byDecade, undated } = useMemo(() => {
    const groups = new Map<number, MemoryTimelineItemDto[]>();
    const withoutDate: MemoryTimelineItemDto[] = [];
    for (const item of data?.items ?? []) {
      const decade = decadeOf(item.takenAt);
      if (decade === null) withoutDate.push(item);
      else groups.set(decade, [...(groups.get(decade) ?? []), item]);
    }
    const firstYear = data?.stats.firstYear;
    const first = firstYear ? Math.floor(firstYear / 10) * 10 : null;
    const range: number[] = [];
    if (first !== null) {
      for (let decade = first; decade <= CURRENT_DECADE; decade += 10) {
        // A filtered view only shows the decades it has something for.
        if (!isFiltered || groups.has(decade)) range.push(decade);
      }
    }
    return { decades: range, byDecade: groups, undated: withoutDate };
  }, [data, isFiltered]);

  const addMemory = (decade?: number) => navigate(decade ? `new?decade=${decade}` : 'new');
  const openMemory = (memoryId: string) => navigate(memoryId);

  const decadeName = (decade: number) =>
    decade < 2000 ? String(decade).slice(-2) : String(decade);

  const scrollTo = (section: string | null) => {
    if (!section) return;
    document
      .getElementById(sectionId(section === UNDATED ? UNDATED : Number(section)))
      ?.scrollIntoView({ behavior: 'smooth' });
  };

  const stats = data?.stats;
  const hasMemories = !!stats && stats.memoryCount > 0;
  const voiceMinutes = Math.round((stats?.voiceSeconds ?? 0) / 60);

  const decadeOptions = [
    ...decades.map(decade => ({ value: String(decade), label: decadeLabel(t, decade) })),
    ...(undated.length > 0
      ? [{ value: UNDATED, label: t('memories.timeline.undatedShort', 'Sans date') }]
      : []),
  ];

  const renderCards = (items: MemoryTimelineItemDto[]) => (
    <div className={styles.cards}>
      {items.map(item => (
        <MemoryCard key={item.id} memory={item} onClick={() => openMemory(item.id)} />
      ))}
    </div>
  );

  return (
    <div className={styles.container}>
      <PageHeader
        label={t('memories.title', 'La mémoire de la maison')}
        rightElement={
          <Button leftSection={<IconPlus size={20} />} onClick={() => addMemory()}>
            {t('memories.add', 'Ajouter un souvenir')}
          </Button>
        }
      />

      {hasMemories && (
        <Group gap="sm" className={styles.filters}>
          <Chip checked={kind === null} onChange={() => setKind(null)} variant="outline">
            {t('memories.filters.all', 'Tout')}
          </Chip>
          {MEMORY_KINDS.map(memoryKind => (
            <Chip
              key={memoryKind}
              checked={kind === memoryKind}
              onChange={() => setKind(memoryKind)}
              variant="outline"
            >
              {memoryKindLabel(t, memoryKind)}
            </Chip>
          ))}
          <Chip checked={withVoice} onChange={setWithVoice} variant="outline">
            {t('memories.filters.withVoice', 'Avec la voix')}
          </Chip>
          {decadeOptions.length > 1 && (
            <Select
              className={styles.decadeSelect}
              placeholder={t('memories.timeline.goTo', 'Aller à une décennie')}
              data={decadeOptions}
              value={null}
              onChange={scrollTo}
              aria-label={t('memories.timeline.goTo', 'Aller à une décennie')}
            />
          )}
        </Group>
      )}

      {hasMemories && (
        <div className={styles.stats}>
          {[
            stats.firstYear
              ? t('memories.stats.since', 'Depuis {{year}}', { year: stats.firstYear })
              : null,
            t('memories.stats.memories', {
              count: stats.memoryCount,
              defaultValue: '{{count}} souvenirs',
            }),
            t('memories.stats.photos', {
              count: stats.photoCount,
              defaultValue: '{{count}} photos',
            }),
            voiceMinutes > 0
              ? t('memories.stats.voice', '{{minutes}} min de voix', { minutes: voiceMinutes })
              : null,
            stats.storytellerCount > 0
              ? t('memories.stats.storytellers', {
                  count: stats.storytellerCount,
                  defaultValue: '{{count}} conteurs',
                })
              : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </div>
      )}

      <div className={styles.content}>
        {isLoading && (
          <div className={styles.loader}>
            <Loader />
          </div>
        )}

        {isError && !data && (
          <Alert color="red">
            {t(
              'memories.timeline.error',
              "La mémoire de la maison n'a pas pu être chargée. Réessayez dans un instant."
            )}
          </Alert>
        )}

        {stats && stats.memoryCount === 0 && (
          <div className={styles.emptyState}>
            <div className={styles.emptyTitle}>
              {t('memories.empty.title', 'Racontez le premier souvenir de la maison')}
            </div>
            <div className={styles.emptyMessage}>
              {t(
                'memories.empty.subtitle',
                'Un mariage dans le jardin, la tempête de 1999, la véranda construite par grand-père : chaque histoire compte.'
              )}
            </div>
            <Button leftSection={<IconPlus size={20} />} onClick={() => addMemory()}>
              {t('memories.add', 'Ajouter un souvenir')}
            </Button>
          </div>
        )}

        {data && data.items.length === 0 && isFiltered && hasMemories && (
          <div className={styles.emptyState}>
            <div className={styles.emptyMessage}>
              {t('memories.filters.noResult', 'Aucun souvenir pour ce filtre.')}
            </div>
            <Button
              variant="outline"
              onClick={() => {
                setKind(null);
                setWithVoice(false);
              }}
            >
              {t('memories.filters.reset', 'Voir tous les souvenirs')}
            </Button>
          </div>
        )}

        {decades.map(decade => {
          const items = byDecade.get(decade) ?? [];
          return (
            <section key={decade} id={sectionId(decade)} className={styles.section}>
              <div className={styles.sectionTitle}>{decadeLabel(t, decade)}</div>
              {items.length === 0 ? (
                <div className={styles.gap}>
                  <div className={styles.emptyMessage}>
                    {t(
                      'memories.timeline.gap',
                      "Personne n'a encore raconté les années {{decade}}",
                      {
                        decade: decadeName(decade),
                      }
                    )}
                  </div>
                  <Button variant="outline" onClick={() => addMemory(decade)}>
                    {t('memories.add', 'Ajouter un souvenir')}
                  </Button>
                </div>
              ) : (
                renderCards(items)
              )}
            </section>
          );
        })}

        {undated.length > 0 && (
          <section id={sectionId(UNDATED)} className={styles.section}>
            <div className={styles.sectionTitle}>
              {t('memories.timeline.undatedShort', 'Sans date')}
            </div>
            {renderCards(undated)}
          </section>
        )}
      </div>

      <Outlet />
    </div>
  );
};

export default MemoriesTimelinePage;
